import {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    clampRange,
    isInRange,
    normalizeRange,
    sliceMatrix,
    toClipboardHtml,
    toTsv,
    writeSheetToClipboard
} from "../../libs/sheetClipboard";

/**
 * 스프레드시트처럼 **드래그로 사각형 영역을 선택해 복사**하는 읽기 전용 그리드. (2026-09-28)
 * 복사한 내용은 엑셀·구글시트에 칸 그대로 붙는다(형식 = `libs/sheetClipboard.js`).
 *
 * - `matrix[0]` = 헤더 행. 헤더도 선택·복사 대상이다(열 이름째로 붙여넣을 수 있게).
 * - 브라우저 기본 텍스트 선택은 끈다(`select-none`). 켜 두면 드래그가 칸 단위가 아니라 글자 단위로 잡혀
 *   행을 넘나들며 뒤섞이고, 엑셀에 붙이면 칸이 어긋난다.
 * - 조작: 드래그 / Shift+클릭으로 범위 확장 / ⌘·Ctrl+A 전체 / ⌘·Ctrl+C 복사 / Esc 해제.
 *   드래그 중 영역 가장자리로 가면 자동 스크롤한다.
 *
 * @param matrix       헤더 포함 2차원 배열(표시값 = 복사값)
 * @param columnWidths 열 너비(px)
 * @param textColumns  엑셀에 '텍스트' 서식으로 붙일 열
 * @param mutedHeaders 헤더를 흐리게 표시할 열(예: 이전 문항)
 * @param emptyMessage 데이터 행이 없을 때 안내
 */
const SurveyResponseGrid = ({matrix, columnWidths, textColumns, mutedHeaders = [], height = 680, emptyMessage}) => {
    const containerRef = useRef(null);
    const headerRef = useRef(null);
    const draggingRef = useRef(false);
    const pointerRef = useRef(null);
    const frameRef = useRef(0);
    const [selection, setSelection] = useState(null); // {anchor:{r,c}, focus:{r,c}}
    const [notice, setNotice] = useState("");

    const rowCount = matrix.length;
    const colCount = matrix[0]?.length ?? 0;
    const tableWidth = columnWidths.reduce((sum, w) => sum + w, 0);
    const range = useMemo(
        () => selection && clampRange(normalizeRange(selection.anchor, selection.focus), rowCount, colCount),
        [selection, rowCount, colCount]
    );

    // 데이터가 바뀌면(다른 프로젝트 등) 이전 선택을 버린다.
    useEffect(() => { setSelection(null); }, [matrix]);

    useEffect(() => {
        if (!notice) return undefined;
        const timer = setTimeout(() => setNotice(""), 2000);
        return () => clearTimeout(timer);
    }, [notice]);

    const cellFromTarget = (target) => {
        const cell = target?.closest?.("[data-r]");
        if (!cell || !containerRef.current?.contains(cell)) return null;
        return {r: Number(cell.dataset.r), c: Number(cell.dataset.c)};
    };

    const moveFocus = useCallback((cell) => {
        setSelection(prev => (!prev || (prev.focus.r === cell.r && prev.focus.c === cell.c))
            ? prev
            : {anchor: prev.anchor, focus: cell});
    }, []);

    /**
     * 포인터 위치의 칸을 찾는다. 포인터가 영역 밖이면 안쪽 가장자리로 끌어와 찾는다(자동 스크롤 중 선택 확장).
     * 고정 헤더·고정 순번 열 뒤로 스크롤된 칸이 있으면 그 경계 바로 안쪽을 본다 — 위로 끌며 스크롤하는 중에
     * 헤더 행이 선택에 끼어들지 않게 하기 위해서다(맨 위까지 스크롤된 뒤에는 헤더도 잡힌다).
     */
    const focusFromPointer = useCallback(() => {
        const container = containerRef.current;
        const pointer = pointerRef.current;
        if (!container || !pointer) return;
        const rect = container.getBoundingClientRect();
        const headerBottom = rect.top + (headerRef.current?.offsetHeight ?? 0);
        const stickyRight = rect.left + (columnWidths[0] ?? 0);
        const minY = container.scrollTop > 0 ? headerBottom + 1 : rect.top + 1;
        const minX = container.scrollLeft > 0 ? stickyRight + 1 : rect.left + 1;
        const x = Math.min(Math.max(pointer.x, minX), rect.left + container.clientWidth - 2);
        const y = Math.min(Math.max(pointer.y, minY), rect.top + container.clientHeight - 2);
        const cell = cellFromTarget(document.elementFromPoint(x, y));
        if (cell) moveFocus(cell);
    }, [columnWidths, moveFocus]);

    const autoScroll = useCallback(() => {
        const container = containerRef.current;
        const pointer = pointerRef.current;
        if (!draggingRef.current || !container || !pointer) {
            frameRef.current = 0;
            return;
        }
        const EDGE = 28;
        const rect = container.getBoundingClientRect();
        const top = rect.top + (headerRef.current?.offsetHeight ?? 0);
        const left = rect.left + (columnWidths[0] ?? 0);
        const right = rect.left + container.clientWidth;
        const bottom = rect.top + container.clientHeight;
        const speed = (distance) => Math.min(40, Math.ceil(distance / 3) + 2);
        let dx = 0;
        let dy = 0;
        if (pointer.y < top + EDGE) dy = -speed(top + EDGE - pointer.y);
        else if (pointer.y > bottom - EDGE) dy = speed(pointer.y - (bottom - EDGE));
        if (pointer.x < left + EDGE) dx = -speed(left + EDGE - pointer.x);
        else if (pointer.x > right - EDGE) dx = speed(pointer.x - (right - EDGE));
        if (dx || dy) {
            container.scrollBy(dx, dy);
            focusFromPointer();
        }
        frameRef.current = requestAnimationFrame(autoScroll);
    }, [columnWidths, focusFromPointer]);

    useEffect(() => {
        const onMove = (e) => {
            if (!draggingRef.current) return;
            pointerRef.current = {x: e.clientX, y: e.clientY};
            focusFromPointer();
            if (!frameRef.current) frameRef.current = requestAnimationFrame(autoScroll);
        };
        const onUp = () => {
            draggingRef.current = false;
            cancelAnimationFrame(frameRef.current);
            frameRef.current = 0;
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            cancelAnimationFrame(frameRef.current);
        };
    }, [autoScroll, focusFromPointer]);

    const onMouseDown = (e) => {
        if (e.button !== 0) return;
        const cell = cellFromTarget(e.target);
        if (!cell) return;
        e.preventDefault(); // 텍스트 선택 시작을 막는다. 대신 포커스는 직접 준다(단축키용).
        containerRef.current?.focus({preventScroll: true});
        draggingRef.current = true;
        pointerRef.current = {x: e.clientX, y: e.clientY};
        setSelection(prev => (e.shiftKey && prev) ? {anchor: prev.anchor, focus: cell} : {anchor: cell, focus: cell});
    };

    const copy = (targetRange) => {
        if (!targetRange) return;
        const part = sliceMatrix(matrix, targetRange);
        const partTextColumns = textColumns.slice(targetRange.left, targetRange.right + 1);
        const ok = writeSheetToClipboard(toTsv(part), toClipboardHtml(part, partTextColumns));
        const rows = targetRange.bottom - targetRange.top + 1;
        const cols = targetRange.right - targetRange.left + 1;
        setNotice(ok ? `${rows}행 × ${cols}열 복사됨` : "복사하지 못했습니다. 다시 시도해 주세요.");
        // 버튼으로 복사해도 포커스를 그리드로 돌려 둔다 — 이어서 Esc·⌘A·⌘C 가 먹게.
        containerRef.current?.focus({preventScroll: true});
    };

    const selectAll = () => {
        if (rowCount === 0 || colCount === 0) return;
        setSelection({anchor: {r: 0, c: 0}, focus: {r: rowCount - 1, c: colCount - 1}});
    };

    const onKeyDown = (e) => {
        const mod = e.metaKey || e.ctrlKey;
        const key = e.key.toLowerCase();
        if (mod && key === "c" && range) {
            e.preventDefault();
            copy(range);
        } else if (mod && key === "a") {
            e.preventDefault();
            selectAll();
        } else if (e.key === "Escape") {
            setSelection(null);
        }
    };

    const fullRange = rowCount && colCount ? {top: 0, bottom: rowCount - 1, left: 0, right: colCount - 1} : null;
    const selectedSize = range ? `${range.bottom - range.top + 1}행 × ${range.right - range.left + 1}열 선택` : "";

    return (
        <div className="flex-col gap-2" style={{minWidth: 0}}>
            <div className="flex items-center justify-between gap-2 text-sm">
                <span className="text-gray-500">
                    드래그로 영역을 선택한 뒤 Ctrl+C(⌘C)를 누르면 엑셀에 그대로 붙여넣을 수 있습니다. Ctrl+A(⌘A) = 전체 선택
                </span>
                <div className="flex items-center gap-2 shrink-0">
                    <span className={notice ? "text-blue-600 font-bold" : "text-gray-500"}>{notice || selectedSize}</span>
                    <button type="button" className="border rounded px-2 py-1 bg-white hover:bg-gray-50 disabled:opacity-40"
                            disabled={!range} onClick={() => copy(range)}>
                        선택 영역 복사
                    </button>
                    <button type="button" className="border rounded px-2 py-1 bg-white hover:bg-gray-50 disabled:opacity-40"
                            disabled={!fullRange} onClick={() => { selectAll(); copy(fullRange); }}>
                        전체 복사
                    </button>
                </div>
            </div>
            <div
                ref={containerRef}
                tabIndex={0}
                role="grid"
                aria-rowcount={rowCount}
                aria-colcount={colCount}
                onMouseDown={onMouseDown}
                onKeyDown={onKeyDown}
                className="relative overflow-auto scrollbar-always border border-gray-300 bg-white select-none outline-none focus:ring-1 focus:ring-blue-300 text-[13px]"
                // contain:inline-size = 표가 아무리 넓어도 이 영역 폭을 부모에 강요하지 않는다(가로 스크롤은 안에서만).
                // scrollbar-always = 앱 전역 스크롤바 숨김(common.css)을 이 영역만 풀고, macOS 에서도 항상 보이게 한다.
                style={{height, width: "100%", maxWidth: "100%", contain: "inline-size"}}
            >
                <table className="border-separate" style={{tableLayout: "fixed", width: tableWidth, borderSpacing: 0}}>
                    <colgroup>
                        {columnWidths.map((w, c) => <col key={c} style={{width: w}}/>)}
                    </colgroup>
                    <thead ref={headerRef} className="sticky top-0 z-20">
                        <GridRow r={0} cells={matrix[0] ?? []} header mutedHeaders={mutedHeaders}
                                 selLeft={range && range.top === 0 ? range.left : -1}
                                 selRight={range && range.top === 0 ? range.right : -1}/>
                    </thead>
                    <tbody>
                        {matrix.slice(1).map((cells, index) => {
                            const r = index + 1;
                            const inRows = range && r >= range.top && r <= range.bottom;
                            return (
                                <GridRow key={r} r={r} cells={cells}
                                         selLeft={inRows ? range.left : -1} selRight={inRows ? range.right : -1}/>
                            );
                        })}
                    </tbody>
                </table>
                {rowCount <= 1 && emptyMessage && (
                    <div className="sticky left-0 p-6 text-center text-gray-500" style={{width: "100%"}}>{emptyMessage}</div>
                )}
            </div>
        </div>
    );
};

/**
 * 한 행. **선택 범위가 이 행에 걸친 열 구간(selLeft~selRight)만 props 로 받는다** — 드래그로 선택이 바뀌어도
 * 걸친 구간이 그대로인 행은 다시 그리지 않는다(응답 수천 행 × 문항 수십 열에서도 드래그가 끊기지 않게).
 */
const GridRow = memo(({r, cells, header = false, mutedHeaders = [], selLeft, selRight}) => {
    const range = selLeft < 0 ? null : {top: r, bottom: r, left: selLeft, right: selRight};
    return (
        <tr>
            {cells.map((value, c) => {
                const selected = isInRange(range, r, c);
                const text = value === null || value === undefined ? "" : String(value);
                const base = header
                    ? `${mutedHeaders[c] ? "text-gray-400" : "text-gray-700"} align-bottom`
                    : "text-gray-900 align-top";
                const background = selected ? "bg-blue-100" : header || c === 0 ? "bg-gray-50" : "bg-white";
                // 순번 열은 가로 스크롤해도 남는다. 헤더의 순번 칸은 위·왼쪽 모두 고정이라 가장 위에 둔다.
                const sticky = c === 0 ? `sticky left-0 ${header ? "z-30" : "z-10"}` : "";
                const Cell = header ? "th" : "td";
                return (
                    <Cell key={c} data-r={r} data-c={c} title={text}
                          className={`${base} ${background} ${sticky} border-r border-b border-gray-200 px-2 py-1 text-left cursor-cell ${c === 0 ? "text-center text-gray-500" : ""}`}>
                        {header
                            ? <div className="line-clamp-2 break-all font-bold">{text}</div>
                            : <div className="truncate">{text}</div>}
                    </Cell>
                );
            })}
        </tr>
    );
});

export default SurveyResponseGrid;
